import React, {createContext, useContext, useState, useCallback} from 'react';
import dynamic from 'next/dynamic';

// Dynamically import HelmProvider to avoid loading Helm CSS unless needed
const HelmProvider = dynamic(
  () => import('mkt-ssr/components/UiEngineProvider/HelmProvider'),
  {
    ssr: true, // Enable SSR for the dynamic component
  },
);

// Dynamically import SailProvider to avoid loading Sail dependencies unless needed
const SailProvider = dynamic(
  () => import('mkt-ssr/components/UiEngineProvider/SailProvider'),
  {
    ssr: true,
  },
);

export type UiEngineType = 'sail' | 'helm' | 'none';

const providers: Record<
  UiEngineType,
  React.ComponentType<{children: React.ReactNode}>
> = {
  sail: SailProvider,
  helm: HelmProvider,
  none: React.Fragment,
};

type UiEngineContextValue = {
  uiEngine: UiEngineType;
  setUiEngine: (engine: UiEngineType) => void;
};

function noopSetUiEngine(_engine: UiEngineType): void {
  // No-op: used as default when no setter is provided
}

const UiEngineContext = createContext<UiEngineContextValue>({
  uiEngine: 'helm',
  setUiEngine: noopSetUiEngine,
});

export function useUiEngine(): UiEngineType {
  return useContext(UiEngineContext).uiEngine;
}

export function useSetUiEngine(): (engine: UiEngineType) => void {
  return useContext(UiEngineContext).setUiEngine;
}

interface UiEngineRootProviderProps {
  children: React.ReactNode;
}

export function UiEngineRootProvider({children}: UiEngineRootProviderProps) {
  const [uiEngine, setUiEngineState] = useState<UiEngineType>('helm');

  const setUiEngine = useCallback((engine: UiEngineType) => {
    setUiEngineState(engine);
  }, []);

  return (
    <UiEngineContext.Provider value={{uiEngine, setUiEngine}}>
      {children}
    </UiEngineContext.Provider>
  );
}

interface UiEngineProviderProps {
  uiEngine: UiEngineType;
  children: React.ReactNode;
}

export function UiEngineProvider({uiEngine, children}: UiEngineProviderProps) {
  const Provider = providers[uiEngine];
  return (
    <UiEngineContext.Provider value={{uiEngine, setUiEngine: noopSetUiEngine}}>
      <Provider>{children}</Provider>
    </UiEngineContext.Provider>
  );
}
